import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import { ServiceConstant } from '../utils/service-constant.util';
import {
  OrderFeedbackForOrder,
  OrderFeedbackListResponse,
  OrderFeedbackSummary,
  RatingSummary,
  ReviewFilters,
  ReviewListResponse,
  RestaurantReview,
} from '../models/review';
import { FNFResponse } from '../models/response';

/**
 * Two systems behind one screen, and they are not interchangeable.
 *
 * Restaurant reviews are public, one per customer, and set the restaurant's
 * rating. The restaurant may reply once and may report — never hide, delete
 * or edit.
 *
 * Order feedback is private, tied to an order, can arrive several times from
 * one guest, and never touches the rating. It is read-only: there is no reply
 * or report endpoint for it at all.
 *
 * The restaurant comes from the token at the gateway, so no restaurant id is
 * sent from here.
 */
@Injectable({
  providedIn: 'root',
})
export class ReviewService {
  private httpClient = inject(HttpClient);

  private readonly reviewsUrl = `${ServiceConstant.apiUrl}/restaurants/reviews`;

  private toParams(source: Record<string, unknown>): HttpParams {
    let params = new HttpParams();
    Object.entries(source).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return params;
  }

  // --- Restaurant reviews -------------------------------------------------

  getReviews(
    filters: ReviewFilters,
    limit = 20,
    cursor: string | null = null,
    sort = 'newest',
  ): Observable<FNFResponse<ReviewListResponse>> {
    const params = this.toParams({
      limit,
      sort,
      cursor,
      rating: filters.rating,
      date_from: filters.date_from,
      date_to: filters.date_to,
      answered: filters.answered,
    });
    return this.httpClient
      .get<FNFResponse<ReviewListResponse>>(this.reviewsUrl, { params })
      .pipe(catchError((error) => of(error)));
  }

  getRatingSummary(): Observable<FNFResponse<RatingSummary>> {
    return this.httpClient
      .get<FNFResponse<RatingSummary>>(`${this.reviewsUrl}/summary`)
      .pipe(catchError((error) => of(error)));
  }

  /** One reply per review, final — there is no edit or delete to pair with it. */
  replyToReview(reviewId: string, text: string): Observable<FNFResponse<RestaurantReview>> {
    return this.httpClient
      .post<FNFResponse<RestaurantReview>>(`${this.reviewsUrl}/${reviewId}/reply`, { text })
      .pipe(catchError((error) => of(error)));
  }

  /** Reporting never hides the review; it asks ForknFind to look. */
  reportReview(
    reviewId: string,
    reason: string,
    detail: string | null,
  ): Observable<FNFResponse<RestaurantReview>> {
    return this.httpClient
      .post<FNFResponse<RestaurantReview>>(`${this.reviewsUrl}/${reviewId}/report`, {
        reason,
        detail,
      })
      .pipe(catchError((error) => of(error)));
  }

  // --- Order feedback -----------------------------------------------------

  getOrderFeedback(
    filters: ReviewFilters,
    limit = 20,
    cursor: string | null = null,
  ): Observable<FNFResponse<OrderFeedbackListResponse>> {
    const params = this.toParams({
      limit,
      cursor,
      rating: filters.rating,
      date_from: filters.date_from,
      date_to: filters.date_to,
      order_number: filters.order_number,
    });
    return this.httpClient
      .get<FNFResponse<OrderFeedbackListResponse>>(`${this.reviewsUrl}/order-feedback`, { params })
      .pipe(catchError((error) => of(error)));
  }

  getOrderFeedbackSummary(
    dateFrom: string | null = null,
    dateTo: string | null = null,
  ): Observable<FNFResponse<OrderFeedbackSummary>> {
    const params = this.toParams({ date_from: dateFrom, date_to: dateTo });
    return this.httpClient
      .get<FNFResponse<OrderFeedbackSummary>>(`${this.reviewsUrl}/order-feedback/summary`, {
        params,
      })
      .pipe(catchError((error) => of(error)));
  }

  /** Feedback on one order, for the Feedback section of Order Detail. */
  getFeedbackForOrder(orderId: string): Observable<FNFResponse<OrderFeedbackForOrder>> {
    return this.httpClient
      .get<FNFResponse<OrderFeedbackForOrder>>(`${this.reviewsUrl}/orders/${orderId}/feedback`)
      .pipe(catchError((error) => of(error)));
  }
}
